import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";
import InputContainer from "./InputContainer";

export type ToggleProps = {
  label?: string | React.ReactNode;
  className?: string;
  value?: boolean;
  onChange?: (nextValue: boolean) => void;
  classes?: {
    track?: string;
    thumb?: string;
    label?: string;
  };
  errorMessage?: string;
} & Omit<
  React.ComponentPropsWithoutRef<"input">,
  | "type"
  | "checked"
  | "defaultChecked"
  | "value"
  | "defaultValue"
  | "onChange"
  | "className"
>;

const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    { label, value = false, onChange, classes, id, className, ...props },
    ref,
  ) => {
    const autoId = React.useId();
    const resolvedId = id ?? autoId;

    return (
      <InputContainer
        bare
        className={twMerge("w-[unset]", className)}
        errorMessage={props.errorMessage}
      >
        <label
          htmlFor={resolvedId}
          className={twMerge(
            "inline-flex items-center gap-3 select-none",
            props.disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
          )}
        >
          <span className="relative inline-flex h-[22px] w-[44px] shrink-0 items-center">
            <input
              {...props}
              ref={ref}
              id={resolvedId}
              type="checkbox"
              role="switch"
              checked={value}
              onChange={(e) => onChange?.(e.target.checked)}
              className="peer sr-only"
            />
            <span
              className={twMerge(
                "pointer-events-none absolute inset-0 rounded-full bg-[#BFBFBF] transition-colors",
                "peer-focus-visible:ring-2 peer-focus-visible:ring-[#1890FF] peer-focus-visible:ring-offset-2",
                "peer-checked:bg-[#1890FF]",
                classes?.track,
              )}
            />
            <span
              className={twMerge(
                "pointer-events-none absolute left-[2px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-[#FFFFFF] transition-transform",
                "peer-checked:translate-x-[22px]",
                classes?.thumb,
              )}
            />
          </span>
          {!!label && (
            <Text level={4} className={twMerge("font-[400]", classes?.label)}>
              {label}
            </Text>
          )}
        </label>
      </InputContainer>
    );
  },
);

Toggle.displayName = "Toggle";

export default Toggle;
